<template>
    <div>
        <modal v-if="showModal" :type="modalType" :data="modalData" @close="closeModal" />

        <!-- Activities with objects -->
        <div class="grid-x">
            <div class="cell small-12 medium-8">
                <h2>Aktiviteter med objekter<div class="object-counter" v-if="objectsWithActivities.allObjectsCount > 0" :title="`${objectsWithActivities.allObjectsCount} objekt${objectsWithActivities.allObjectsCount > 1 ? 'er' : ''} tilknyttet opgaven`">{{ objectsWithActivities.allObjectsCount }}</div></h2>
                <h5 v-if="fullAddress" style="font-weight: normal; font-size: 1rem; margin-top: -5px;">{{fullAddress}} - <a :href="`https://www.google.com/maps?daddr=${fullAddress.replace(/ /g, '+')}`" target="_blank" style="font-size: 0.8rem;">Rutevejledning &raquo;</a></h5>
            </div>
            <div class="cell small-12 medium-4 flexbox-end">
                <button class="button-blue" v-if="attributesUpdateable" :disabled="requisition && requisition.status && (requisition.status.toLowerCase() === 'ny' || requisition.status.toLowerCase() === 'færdigmeldt') && requisitionStatus === null" @click="editAttributes()">Opdater attributter</button>
            </div>
        </div>

        <template v-if="filteredObjectList && filteredObjectList.length > 0" v-for="(object, index) in filteredObjectList">
            <div :id="`activity-${object.objectId}`" class="activity-container" :class="{'disabled': requisition && requisition.status && (requisition.status.toLowerCase() === 'ny' || requisition.status.toLowerCase() === 'færdigmeldt') && requisitionStatus === null}" :key="object.objectId">
                <a href="#" class="activity-header flexbox align-items" :class="{'object-clickable': object.geomWkt}" @click.prevent="zoomToObject(object)">
                    <icon v-if="object.geomWkt" class="medium-svg fill-primary" name="location" />
                    <icon v-else class="medium-svg fill-selected-dark-gray" name="asset" />
                    <span class="object-name" v-if="object && object.name">{{ object.name }}</span>
                    <span class="object-name" v-else-if="object && object.layerName">{{object.layerName}}</span>
                    <span class="object-name" v-else-if="object && object.keyValue">{{object.keyValue}}</span>
                    <span class="object-description" v-if="object && object.objectDescription && object.objectDescription !== '[]'" v-html="formatDescription(object)"></span>
                    <span v-if="object && !object.name && !object.layerName && !object.keyValue"><em>Intet navn eller id på objekt</em></span>
                </a>
                <div class="grid-x align-items" v-if="activity && activity.activityId && activity.requisitionStatus" v-for="activity in object.activityList" :key="activity.activityId">
                    <div class="cell small-8 large-10 list-row activity" style="margin-right: 0px;" @click="openModal(activity, object)" :title="activity.description">
                        <div class="grid-x align-items">
                            <div class="cell large-2 hide-for-small-only">
                                <icon v-if="activity.createdByContactor" class="task-check medium-svg fill-primary" name="user-reverse-helmet" />
                                <icon v-else class="task-check medium-svg fill-primary" name="task-check" />
                            </div>
                            <div class="cell auto item truncate-fade" style="margin-right: 4px;">
                                <span>{{activity.activityType}}</span>
                                <span>{{activity.description ? activity.description : 'Ingen bemærkning'}}</span>
                            </div>
                            <div class="cell auto item truncate-fade">
                                <span v-if="!activity.startDate && !activity.latestDate" style="display: inline-block; font-size: 0.9rem; color: #82868d; font-weight: 400;">Ingen start- eller slutdato</span>
                                <span v-else-if="activity.startDate && !activity.latestDate" style="display: inline-block">Start: {{ activity.startDate | formatDateShort }}</span>
                                <span v-else-if="!activity.startDate && activity.latestDate" style="display: inline-block">Slut: {{ activity.latestDate | formatDateShort }}</span>
                                <span v-if="activity.startDate && activity.latestDate">Start: {{ activity.startDate | formatDateShort }}</span>
                                <span v-if="activity.startDate && activity.latestDate">Slut: {{ activity.latestDate | formatDateShort }}</span>
                            </div>

                            <div class="cell large-2 item truncate-fade hide-for-small-only">
                                <icon class="small-svg fill-primary" v-if="activity.reportNote" name="bubble" />
                                <div class="activity-object-note" v-if="activity.reportNote">{{ activity.reportNote }}</div>
                            </div>
                            <div v-if="activity.reportNote" class="cell small-2 show-for-small-only">
                                <icon class="small-svg fill-primary" name="bubble" />
                            </div>
                        </div>
                    </div>
                    <div class="cell small-4 large-2 flexbox-end">
                        <div class="input-container trigger">
                            <input class="triggerBox triggerDeny" type="checkbox" @change="setCheckboxDeny($event, object, activity)" :checked="activity.reportWorkDone === false" :id="activity.activityId + object.objectId + 'deny'" value="activity.activityId">
                            <label :for="activity.activityId + object.objectId + 'deny'" class="checker checker-deny"></label>
                            <!-- <input type="checkbox" class="deny" @change="setCheckboxDeny(object, activity)" :checked="activity.reportWorkDone === false" :id="activity.activityId + 'deny'" value="activity.activityId">                         -->
                        </div>
                        <div class="input-container trigger">
                            <input class="triggerBox triggerAccept" type="checkbox" @change="setCheckboxAccept($event, object, activity)" :checked="activity.reportWorkDone === true" :id="activity.activityId + object.objectId + 'accept'" value="activity.activityId">
                            <label :for="activity.activityId + object.objectId + 'accept'" class="checker checker-accept"></label>
                            <!-- <input type="checkbox" class="accept" @change="setCheckboxAccept(object, activity)" :checked="activity.reportWorkDone === true" :id="activity.activityId + 'accept'" value="activity.activityId"> -->
                        </div>
                    </div>
                </div>
            </div>
            <hr v-if="filteredObjectList.length > index + 1" class="divider"/>
        </template>

        <!-- Activities without objects -->
        <h2 v-if="objectsWithActivities.activitiesWithoutObjectsList && objectsWithActivities.activitiesWithoutObjectsList.length > 0">Aktiviteter uden objekter</h2>
        <div v-if="objectsWithActivities.activitiesWithoutObjectsList && objectsWithActivities.activitiesWithoutObjectsList.length > 0" class="activity-container" :class="{'disabled': requisition && requisition.status && requisition.status.toLowerCase() === 'ny' && requisitionStatus === null}" v-for="activity in objectsWithActivities.activitiesWithoutObjectsList" :key="activity.activityId">
            <div class="grid-x align-items">
                <div class="cell small-8 large-8 list-row activity" @click="openModal(activity)">
                    <div class="grid-x align-items">
                        <div class="cell large-2 hide-for-small-only">
                            <icon class="task-check medium-svg fill-primary" name="task-check" />
                        </div>
                        <div class="cell auto item truncate-fade" style="margin-right: 4px;">
                            <span>{{activity.activityType}}</span>
                            <span>{{activity.description ? activity.description : 'Ingen bemærkning'}}</span>
                        </div>
                        <div class="cell large-3 item truncate-fade hide-for-small-only">
                            <span>Note</span>
                            <span>{{activity.reportNote ? activity.reportNote : 'Ingen note'}}</span>
                        </div>
                        <div v-if="activity.reportNote" class="cell small-2 show-for-small-only">
                            <icon class="small-svg fill-primary" name="bubble" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import modal from './../../shared/modal/modal.vue'

export default {
    data () {
        return {
            modalData: null,
            modalType: null,
            showModal: false,
            checkboxObj: [], // nye
            checkboxes: [],
            checkboxValues: {},
            reportObject: {
                requisitionId: '',
                activityIdList: [],
                objectIdList: [],
                reportWorkDoneList: [],
                objectActivityVersionStampList: []
            }
        }
    },
    props: {
        fullAddress: String,
        requisition: Object,
        requisitionStatus: String
    },
    components: {
        modal
    },
    computed: {
        ...mapGetters({
            objectsWithActivities: 'reportItemWhole',
            objectActivityVersionStamp: 'getObjectActivityVersionStamp',
            getReportFullObjectList: 'getReportFullObjectList',
            applicationSettings: 'getApplicationSettings',
            selectedSupplyType: 'selectedSupplyType'
        }),
        filteredObjectList () {
            // console.log('filteredObjectList, this.requisition', this.requisition)
            // console.log('filteredObjectList, this.objectsWithActivities', this.objectsWithActivities)
            // console.log('filteredObjectList, this.objectsWithActivities.objectList', this.objectsWithActivities.objectList)

            return this.objectsWithActivities.objectList
        },
        attributesUpdateable () {
            const mapService = this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].mapServiceList.find((service) => {
                return service.type === 'ogc' || service.type === 'arcgis' || service.type === 'kortinfo' || service.type === 'ogckortinfo'
            })

            if (mapService && mapService.wfsUpdateable) {
                return true
            }

            return false
        }
    },
    methods: {
        ...mapActions({
            setReport: 'setReport',
            toggleMapState: 'toggleMapState',
            clearReportObjectActivitiesFilter: 'clearReportObjectActivitiesFilter'
        }),
        setCheckboxDeny ($event, object, activity) {
            this.setReport({
                requisitionId: this.$route.params.id,
                objectIdList: [object.objectId],
                activityIdList: [activity.activityId],
                reportWorkDoneList: [($event.target.checked ? false : null)],
                objectActivityVersionStampList: [activity.objectActivityVersionStamp]
            })
                .then(() => {
                    for (let j in this.objectActivityVersionStamp) {
                        if (activity.activityId === this.objectActivityVersionStamp[j].activityId) {
                            activity.objectActivityVersionStamp = this.objectActivityVersionStamp[j].objectActivityVersionStamp
                            if (activity.reportWorkDone === false) {
                                activity.reportWorkDone = null
                            } else {
                                activity.reportWorkDone = false
                            }
                        }
                    }
                })
                .then(() => {
                    this.updateObjectStatus(object)
                    this.$parent.updateObjectSummary()
                })
        },
        setCheckboxAccept ($event, object, activity) {
            this.setReport({
                requisitionId: this.$route.params.id,
                objectIdList: [object.objectId],
                activityIdList: [activity.activityId],
                reportWorkDoneList: [($event && $event.target.checked ? true : null)],
                objectActivityVersionStampList: [activity.objectActivityVersionStamp]
            })
                .then(() => {
                    for (let j in this.objectActivityVersionStamp) {
                        if (activity.activityId === this.objectActivityVersionStamp[j].activityId) {
                            activity.objectActivityVersionStamp = this.objectActivityVersionStamp[j].objectActivityVersionStamp
                            if (activity.reportWorkDone === true) {
                                activity.reportWorkDone = null
                            } else {
                                activity.reportWorkDone = true
                            }
                        }
                    }
                })
                .then(() => {
                    this.updateObjectStatus(object)
                    this.$parent.updateObjectSummary()
                })
        },
        updateObjectStatus (object) {
            let objectList = ''
            if (this.getReportFullObjectList.length > 0) {
                // If single object is selected
                objectList = this.getReportFullObjectList
            } else {
                objectList = this.objectsWithActivities.objectList
            }
            const objectIndex = objectList.findIndex(o => o.objectId === object.objectId)
            objectList[objectIndex].activityList = object.activityList
            this.$parent.$refs.baseMap.highlightObjects(objectList)
        },
        zoomToObject (object) {
            if (object.geomWkt) {
                if (screen.width <= 760) {
                    this.toggleMapState(true)
                }
                // Bad idea to use parent here? I think so.
                this.$parent.$refs.baseMap.zoomAndHighlightObject([object])
            }
        },
        openModal (activity, object) {
            this.modalType = 'requisition-modal-activity'
            this.modalData = {
                activity: activity,
                objectId: object ? object.objectId : null,
                requisitionId: this.objectsWithActivities.requisition.requisitionId
            }
            this.showModal = true
        },
        openAttributeModal (selectedLayer) {
            // Find the possible values and types for the properties of the selected layer
            const mapService = this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].mapServiceList.find((service) => {
                return (service.type === 'ogc' || service.type === 'arcgis' || service.type === 'kortinfo' || service.type === 'ogckortinfo') && service.layers.find(layer => layer.name === selectedLayer)
            })
            // Make objects to pass into the modal
            const features = [{
                name: 'Andre',
                features: null
            }]

            if (mapService && mapService.layers) {
                const layer = mapService.layers.find(layer => layer.name === this.filteredObjectList[0].layerName)

                if (layer) {
                    features[0].features = this.filteredObjectList.filter((obj) => {
                        if (selectedLayer) {
                            return obj.layerName === selectedLayer
                        } else {
                            return true
                        }
                    }).map((obj, i) => {
                        return {
                            name: `Objekt ${(i + 1)}`,
                            layerId: layer.id,
                            layerName: layer.name,
                            properties: [{
                                name: layer.objectIdPropertyName,
                                value: obj.keyValue
                            }],
                            objectIdPropertyName: layer.objectIdPropertyName,
                            dataSourceId: obj.datasourceId,
                            geometry: obj.geomWkt
                        }
                    })
                }
            }

            const data = {
                layers: features,
                editOnLoad: true
            }

            this.modalType = 'properties-modal'
            this.modalData = data
            this.showModal = true
        },
        editAttributes () {
            // Check if activity has multiple layers
            const validateMultipleLayers = this.filteredObjectList.every(obj => obj.layerName === this.filteredObjectList[0].layerName)
            if (!validateMultipleLayers) {
                // Choose a layer..
                // Filter the objects to only contain objects from selected layer and pass into modal
                this.modalType = 'requisition-modal-layers'
                this.modalData = {
                    layers: [...new Set(this.filteredObjectList.map(obj => obj.layerName))],
                    openAttributeModal: this.openAttributeModal
                }
                this.showModal = true
            } else {
                this.openAttributeModal(this.filteredObjectList[0].layerName)
            }
        },
        closeModal () {
            this.showModal = false
        },
        formatDescription (object) {
            if (object.layerName) {
                const mapService = this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].mapServiceList.find((service) => {
                    return (service.type === 'ogc' || service.type === 'arcgis' || service.type === 'kortinfo' || service.type === 'ogckortinfo') && service.layers.find(layer => layer.name === object.layerName || layer.name === object.LayerName)
                })

                if (mapService && mapService.layers) {
                    const layer = mapService.layers.find(layer => layer.name === object.layerName || layer.name === object.LayerName)

                    if (layer && layer.displayObjectProperties) {
                        const displayObjectProperties = layer.displayObjectProperties
                        let formatted = ''

                        if (displayObjectProperties) {
                            formatted += '('
                        }
                        try {
                            JSON.parse(object.objectDescription).forEach((object, index) => {
                                if (displayObjectProperties && object && object.Name && object.Value) {
                                    if (index > 0 && formatted !== '(') {
                                        formatted += ', '
                                    }
                                    if (displayObjectProperties.find(property => property.name === object.Name) && displayObjectProperties.find(property => property.name === object.Name).displayText) {
                                        formatted += '<span class="object-description__label">' + displayObjectProperties.find(property => property.name === object.Name).displayText + ':</span>'
                                    } else {
                                        formatted += '<span class="object-description__label">' + object.Name + ':</span>'
                                    }
                                    formatted += ' <span class="object-description__value">' + object.Value + '</span>'
                                }
                            })

                            if (object.objectLength) {
                                if (displayObjectProperties) {
                                    formatted += ', '
                                }
                                formatted += '<span class="object-description__label">Længde:</span>'
                                formatted += ' <span class="object-description__value">' + object.objectLength + '</span>'
                            }
                        } catch (error) {
                            console.error('Format object description', error, object.objectDescription)
                            formatted += object.objectDescription
                        }
                        if (displayObjectProperties) {
                            formatted += ')'
                        }
                        return formatted
                    }
                }
            }

            return '<span class="object-description__label">' + object.objectDescription + '</span>'
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/shared/list/list-objects.vue